import React, { useEffect, useState } from "react";
import { Table, TableColumnType } from "antd";
import { Checkbox, Popover, Skeleton, Tooltip } from "antd";
import {
  FaPaperclip,
  FaEye,
  FaPaperPlane,
} from "react-icons/fa6";
import ProfileModal from "../Modals/ProfileModal";
import { useDispatch, useSelector } from "react-redux";
import { ownerSelector, prospectsSelector, RootState } from "../../store/store";
import { RxAvatar } from "react-icons/rx";
import {
  setCurrentProspect,
  setProspects,
  setProspectsCount,
  setSelectedProspects,
} from "../../store/slices/prospectsSlice";
import ProspectsSettings from "../Modals/ProspectsSettings";
import { Prospect, ProspectTableColumns } from "../../types";
import Tags from "../Dropdowns/Tags";
import TagSwatch from "../tags/TagSwatch";
import { fetchProspects } from "../../utils";
import { toast } from "react-toastify";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import LeadSenderAvatar from "../ui/LeadSenderAvatar";
import { ServerTablePagination } from "../ui";

type ProspectTableProps = {
  listChanged?: boolean;
  selectedStatus?: string;
  selectedTagId?: string;
  isListSwitchLoading?: boolean;
};

const ProspectTable: React.FC<ProspectTableProps> = ({
  listChanged,
  selectedStatus = "all",
  selectedTagId = "all",
  isListSwitchLoading = false,
}: ProspectTableProps) => {
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const { prospects, prospectsCount, selectedProspects, inCampaign, listName } =
  useSelector(prospectsSelector);
  const [totalProspects, setTotalProspects] = useState(prospectsCount);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { listId, owner, currentWorkspace, currentUser } =
    useSelector(ownerSelector);
  const workspace =
    typeof currentWorkspace === "string"
      ? (() => {
          try {
            return JSON.parse(currentWorkspace || "null");
          } catch {
            return null;
          }
        })()
      : currentWorkspace;
  const workspaceId = workspace?._id as string;
  const userId = currentUser?.userId as string;
  const [filteredProspects, setFilteredProspects] =
    useState<Prospect[]>(prospects);
  const [filterOptions, setFilterOptions] = useState<string[]>([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [tagPopoverProspectId, setTagPopoverProspectId] = useState("");
  const [isFetchingAll, setIsFetchingAll] = useState(false);
  // const [allSelected, setAllSelected] = useState(false);
  const [selectedProspectsArray, setSelectedProspectsArray] = useState<
    string[]
  >([]);
  const [headerSelectAllChecked, setHeaderSelectAllChecked] = useState(false);
  // const [isTagAdded, setIsTagAdded] = useState(false);

  const handleProspectFetch = async (page: number, limit: number) => {
    const loader = toast.loading("Fetching prospects...");
    try {
      const getProspects = await fetchProspects(
        owner?._id as string,
        listId,
        page,
        limit,
        userId,
        workspaceId,
        { tagId: selectedTagId }
      );
      if (getProspects.prospects.length === 0) {
        toast.dismiss(loader);
        return toast.error("No prospects found");
      }

      toast.dismiss(loader);
      dispatch(setProspects(getProspects.prospects));
      dispatch(setProspectsCount(getProspects.totalProspects));
      return getProspects.prospects;
    } catch (error) {
      toast.dismiss(loader);
      toast.error("Failed to fetch prospects");
      return [];
    }
  };

  const filterProspects = (prospectList: Prospect[]) => {
    let prospectsForFilter: Prospect[] = prospectList;
      if (selectedStatus === "connected") {
        prospectsForFilter = prospectsForFilter.filter(
          (prospect) => prospect.connected === "ACCEPTED"
        );
      }
      if (selectedStatus === "not_connected") {
        prospectsForFilter = prospectsForFilter.filter(
          (prospect) => prospect.connected !== "ACCEPTED"
        );
      }
      if (selectedStatus === "has_email") {
        prospectsForFilter = prospectsForFilter.filter(
          (prospect) => prospect.contactInfo.emailAddress
        );
      }
      if (selectedStatus === "is_premium") {
        prospectsForFilter = prospectsForFilter.filter(
          (prospect) => prospect.isPremium
        );
      }
      if (selectedTagId !== "all") {
        prospectsForFilter = prospectsForFilter.filter((prospect) =>
          (prospect.tags || []).some((tag) => tag._id === selectedTagId)
        );
      }
      if (filterOptions.includes("INVITATION_SENT")) {
        prospectsForFilter = prospectsForFilter.filter(
          (prospect) =>
            prospect.connected === "PENDING" ||
            prospect.connected === "ACCEPTED"
        );
      }

      if (filterOptions.includes("PROFILE_VISITED")) {
        prospectsForFilter = prospectsForFilter.filter(
          (prospect) => prospect.profileVisited
        );
      }

      if (filterOptions.includes("MESSAGE_SENT")) {
        prospectsForFilter = prospectsForFilter.filter(
          (prospect) => prospect.messageSent
        );
      }

      return prospectsForFilter;
  }

  const handleSelectAll = async (selected: boolean) => {
    if (selected && prospectsCount > prospects.length) {
      setIsFetchingAll(true);
      const loader = toast.loading("Fetching all prospects...");

      try {
        // Fetch all prospects in one go
        const allProspects = await fetchProspects(
          owner?._id as string,
          listId,
          1,
          prospectsCount,
          userId,
          workspaceId,
          { tagId: selectedTagId }
        );

        if (allProspects.prospects.length > 0) {
          dispatch(setProspects(allProspects.prospects));
          const newList = filterProspects(allProspects.prospects);
          setFilteredProspects(allProspects.prospects);
          setTotalProspects(allProspects.prospects.length);
          setSelectedProspectsArray(
            newList.map((p: any) => p._id)
          );
        }

        toast.dismiss(loader);
      } catch (error) {
        toast.dismiss(loader);
        toast.error("Failed to fetch all prospects");
      }

      setIsFetchingAll(false);
    } else {
      // If deselecting all or if all prospects are already loaded
      setSelectedProspectsArray(selected ? prospects.map((p) => p._id) : []);
    }

  };

  const columns: TableColumnType<ProspectTableColumns>[] = [
    {
      title: "Status",
      dataIndex: "statuses",
      width: 115,
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 410,
    },
    {
      title: "Company/Job",
      dataIndex: "job",
      width: 410,
    },
    {
      title: "Sender",
      dataIndex: "sender",
      width: 90,
    },
    {
      title: "Tags",
      dataIndex: "tags",
      width: 120,
    },
    {
      title: "Last Action",
      dataIndex: "actions",
      width: 120,
    },
  ];

  const tableData: ProspectTableColumns[] = filteredProspects.map(
    (prospect) => {
      return {
        key: prospect._id,
        name: (
          <div
            onClick={() => {
              dispatch(setCurrentProspect(prospect));
              setShowProfileModal(true);
            }}
            className="flex font-primary  items-center gap-2 cursor-pointer"
          >
            {prospect.image_url ? (
              <img
                src={prospect.image_url}
                alt=""
                className="w-8 h-8 rounded-full"
              />
            ) : (
              <RxAvatar
                className="w-10 h-10 text-mainColor cursor-pointer"
                // size={20}
              />
            )}
            <p className="whitespace-nowrap text-xs">
              {prospect.full_name?.length > 20 ? (
                <Popover content={prospect.full_name}>
                  {prospect.full_name.slice(0, 20) + "..."}
                </Popover>
              ) : (
                prospect.full_name
              )}
            </p>
          </div>
        ),
        job: (
          <div
            onClick={() => {
              dispatch(setCurrentProspect(prospect));
              setShowProfileModal(true);
            }}
            className="flex gap-2 font-primary"
          >
            {prospect.current_company_name && prospect.title ? (
              <p className="text-xs">
                {prospect.title} at {prospect.current_company_name}
              </p>
            ) : (
              <p className="text-xs">{prospect.headline}</p>
            )}
          </div>
        ),
        sender: (
          <div className="flex w-full items-center justify-center">
            <Tooltip
              title={
                prospect.sender
                  ? `${(prospect.sender.name || "Sender").trim()}${
                      prospect.sender.isDisconnected ? " (disconnected)" : ""
                    }`
                  : undefined
              }
            >
              <span className="inline-flex">
                <LeadSenderAvatar
                  picture={prospect.sender?.picture}
                  disconnected={Boolean(prospect.sender?.isDisconnected)}
                  className="h-8 w-8"
                />
              </span>
            </Tooltip>
          </div>
        ),
        statuses: (
          <div className="flex items-center gap-2">
            {(() => {
              const isEnabled = prospect.connected === "ACCEPTED";
              const statusLabel = isEnabled
                ? "Connected on LinkedIn"
                : "Not connected to LinkedIn";

              return (
                <Tooltip title={statusLabel} color="var(--color-dark-blue)">
                  <button
                    type="button"
                    aria-label={statusLabel}
                    disabled
                    className={`relative h-[14px] w-7 rounded-[16px] ${
                      isEnabled ? "bg-brand-blue" : "bg-light-grey"
                    } cursor-default`}
                  >
                    <span
                      className={`absolute top-[2px] h-[10px] w-[10px] rounded-full bg-white ${
                        isEnabled ? "left-4" : "left-[2px]"
                      }`}
                    />
                  </button>
                </Tooltip>
              );
            })()}
          </div>
        ),
        actions: (
          <div className="flex w-full items-center justify-center">
            <div className="flex items-center gap-[6px]">
              <Tooltip
                title={
                  prospect.connected === "PENDING" || prospect.connected === "ACCEPTED"
                    ? "Invitation Sent"
                    : "No Invitation Sent"
                }
              >
                <span className="inline-flex">
                  <FaPaperclip
                    className={
                      prospect.connected === "PENDING" || prospect.connected === "ACCEPTED"
                        ? "text-brand-blue"
                        : "text-dark-grey"
                    }
                    size={13}
                  />
                </span>
              </Tooltip>
              <Tooltip title={prospect.profileVisited ? "Profile Visited" : "Profile Not Visited"}>
                <span className="inline-flex">
                  <FaEye
                    size={13}
                    className={prospect.profileVisited ? "text-brand-blue" : "text-dark-grey"}
                  />
                </span>
              </Tooltip>
              <Tooltip title={prospect.messageSent ? "Message Sent" : "No Message Sent"}>
                <span className="inline-flex">
                  <FaPaperPlane
                    size={13}
                    className={prospect.messageSent ? "text-brand-blue" : "text-dark-grey"}
                  />
                </span>
              </Tooltip>
            </div>
          </div>
        ),
        tags: (
          <div className="flex w-full items-center justify-center">
            <Popover
              open={tagPopoverProspectId === prospect._id}
              onOpenChange={(next) => {
                setTagPopoverProspectId(next ? prospect._id : "");
              }}
              trigger="click"
              placement="bottomLeft"
              overlayInnerStyle={{ padding: 0 }}
              content={
                <Tags
                  prospectId={prospect._id}
                  listId={listId}
                  assignedTagIds={(prospect.tags || []).map((t) => t._id)}
                  prospectsPage={currentPage}
                  prospectsPageSize={pageSize}
                  onClose={() => setTagPopoverProspectId("")}
                />
              }
            >
              <button
                type="button"
                className="flex h-[22px] max-w-full items-center gap-[6px] rounded-[4px] px-0.5 hover:bg-primary/80"
              >
                <div className="flex items-center gap-1">
                  {(prospect.tags || []).slice(0, 3).map((tag) => (
                    <TagSwatch key={tag._id} color={tag.color} size="sm" />
                  ))}
                </div>
                {(prospect.tags || []).length > 3 ? (
                  <span className="shrink-0 rounded-[6px] bg-primary px-1.5 py-0.5 text-[10px] font-normal leading-[12px] text-dark-grey">
                    +{(prospect.tags || []).length - 3}
                  </span>
                ) : null}
                <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-light-grey text-[11px] font-normal leading-none text-dark-grey">
                  +
                </span>
              </button>
            </Popover>
          </div>
        ),
      };
    }
  );

  const visibleRowKeys = tableData.map((row) => String(row.key));

  // const tableProps = {
  //   columns,
  //   dataSource: tableData,
  //   pagination: {
  //     position: ["bottomRight"],
  //     total: prospectsCount,
  //     showSizeChanger: false,
  //     pageSize: pageSize,
  //     size: "small",
  //     onChange: async (page: number, pageSize: number) => {
  //       if (!isFetchingAll) {
  //         // Prevent pagination while fetching all
  //         await handleProspectFetch(page, pageSize);
  //       }
  //     },
  //   },
  //   rowSelection:
  //     !inCampaign && listName !== "All Prospects"
  //       ? {
  //           type: "checkbox",
  //           selectedRowKeys: selectedProspectsArray,
  //           onChange: (
  //             selectedRowKeys: React.Key[],
  //             selectedRows: ProspectTableColumns[]
  //           ) => {
  //             setSelectedProspectsArray(selectedRowKeys as string[]);
  //           },
  //           onSelectAll: handleSelectAll,
  //           selections: [
  //             {
  //               key: "all",
  //               text: `Select all ${prospectsCount} prospects`,
  //               onSelect: () => handleSelectAll(true),
  //             },
  //             {
  //               key: "none",
  //               text: "Clear selection",
  //               onSelect: () => handleSelectAll(false),
  //             },
  //           ],
  //         }
  //       : undefined,
  //   loading: isFetchingAll,
  // };
  useEffect(() => {
    setPageSize(10);
    setCurrentPage(1);
    setFilterOptions([]);
    setHeaderSelectAllChecked(false);
    setSelectedProspectsArray([]);
  }, [listId]);

  useEffect(() => {
    dispatch(setSelectedProspects(selectedProspectsArray));

    const handleFilter = async () => {
      try {
        const hasClientFilters = filterOptions.length > 0 || selectedStatus !== "all";
        if (!hasClientFilters) {
          setFilteredProspects(prospects);
          setTotalProspects(prospectsCount);
          // toast.dismiss(loading);
          return;
        }
        const loading = toast.loading("Filtering prospects...");

        const listForFilter =
          prospects.length === prospectsCount
            ? prospects
            : (
                await fetchProspects(
            owner?._id as string,
            listId,
            1,
            prospectsCount,
            userId,
            workspaceId,
            { tagId: selectedTagId }
                )
              ).prospects;

        const prospectsForFilter = filterProspects(listForFilter);
        setFilteredProspects(prospectsForFilter)
        setTotalProspects(prospectsForFilter.length);

        toast.dismiss(loading);
      } catch (error) {
        console.log(error);
        // toast.dismiss(loading);
      }
    };

    handleFilter();
  }, [prospects, filterOptions, selectedStatus, selectedTagId, selectedProspectsArray, dispatch, listId, owner?._id, prospectsCount]);

  return (
    <>
      {isListSwitchLoading ? (
        <div className="flex h-full min-h-[420px] flex-col gap-2 rounded-[8px] bg-white px-0 py-0">
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton.Input key={idx} active className="!h-10 !w-full !rounded-[6px]" />
          ))}
        </div>
      ) : (
        <div
          id="prospects"
          className="prospects relative flex h-full min-h-0 flex-col"
        >
          <div className="min-h-0 flex-1 overflow-auto">
            <Table
              id="prospects"
              className="leads-detail-table text-xs font-inter"
              columns={columns}
              dataSource={tableData}
              pagination={false}
              locale={{ emptyText: "No leads yet" }}
              rowSelection={
                !inCampaign && listName !== "All Prospects"
                  ? {
                      type: "checkbox",
                      columnTitle: (
                        <Checkbox
                          checked={headerSelectAllChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProspectsArray(visibleRowKeys);
                              setHeaderSelectAllChecked(true);
                            } else {
                              setSelectedProspectsArray([]);
                              setHeaderSelectAllChecked(false);
                            }
                          }}
                        />
                      ),
                      selectedRowKeys: selectedProspectsArray,
                      onChange: (selectedRowKeys: React.Key[]) => {
                        setSelectedProspectsArray(selectedRowKeys.map((k) => String(k)));
                      },
                      selections: [
                        {
                          key: "all",
                          text: `Select all ${prospectsCount} prospects`,
                          onSelect: () => handleSelectAll(true),
                        },
                        {
                          key: "none",
                          text: "Clear selection",
                          onSelect: () => handleSelectAll(false),
                        },
                      ],
                    }
                  : undefined
              }
              loading={isFetchingAll}
            />
          </div>
          <ServerTablePagination
            className="mt-3 shrink-0 border-t border-light-grey bg-white pt-3"
            page={currentPage - 1}
            pageSize={pageSize}
            total={totalProspects}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={async (nextPage) => {
              const oneBasedPage = nextPage + 1;
              setCurrentPage(oneBasedPage);
              if (!isFetchingAll) await handleProspectFetch(oneBasedPage, pageSize);
            }}
            onPageSizeChange={async (nextSize) => {
              setPageSize(nextSize);
              setCurrentPage(1);
              if (!isFetchingAll) await handleProspectFetch(1, nextSize);
            }}
          />
        </div>
      )}

      {showProfileModal && (
        <ProfileModal onClose={() => setShowProfileModal(false)} />
      )}
      {selectedProspects.length > 0 && (
        <ProspectsSettings setSelectedArray={setSelectedProspectsArray} />
      )}
    </>
  );
};

export default ProspectTable;
